/**
 * Atlas PM · 任务详情（设计稿 12）· 中列：产出物齐套（⭐ P6.51 新口径）
 *
 * 产出物契约已整体取消，本列改为展示「按要素『需要附件』核算」的齐套率：
 *   齐套率 = 已上传至少 1 个附件的「需附件要素」数 / 全部「需附件要素」数
 *
 * ⚠️ 无任何 requireAttachment=true 的要素时显示「—」（不是 0%）。
 * ⚠️ 只读展示：附件的上传/删除在左列「指标与要素」内的 ElementProgressControl 完成。
 */

import Badge from '../../components/Badge';
import EmptyState from '../../components/EmptyState';
import type { CompletenessInfo } from '@atlaspm/shared';

export interface DetailCompletenessColumnProps {
  completeness?: CompletenessInfo;
  taskState: string;
}

function pctText(pct: number | null | undefined): string {
  return pct === null || pct === undefined ? '—' : `${pct.toFixed(1)}%`;
}

export default function DetailCompletenessColumn({
  completeness,
  taskState,
}: DetailCompletenessColumnProps) {
  const pct = completeness?.completenessPct;
  const requiredTotal = completeness?.requiredTotal ?? 0;
  const requiredSatisfied = completeness?.requiredSatisfied ?? 0;
  const elements = completeness?.elements ?? [];
  const allOk = requiredTotal > 0 && requiredSatisfied === requiredTotal;

  return (
    <section className="dt-card dcol" data-col="completeness" aria-label="产出物齐套">
      <header className="dt-card__head">
        <h3 className="dt-card__title">产出物齐套 · {pctText(pct)}</h3>
        {requiredTotal > 0 && (
          <span className="dt-card__extra">
            {requiredSatisfied}/{requiredTotal} 需附件要素
          </span>
        )}
      </header>

      <div className="dt-card__body">
        {(taskState === 'FROZEN' || taskState === 'ARCHIVED') && (
          <div className="dt-note dt-note--violet">
            <div className="dt-note__lock">🔒 附件已加合规保留锁，冻结期内不可删除或覆盖</div>
          </div>
        )}

        {requiredTotal === 0 ? (
          <EmptyState
            icon="◇"
            title="无需附件要素"
            hint="任务中没有要素勾选「需要附件」，齐套率显示为「—」。"
          />
        ) : (
          <>
            <div className="dt-contract-sum">
              <div className="dt-contract-sum__box">
                <div className="dt-contract-sum__label">需附件要素</div>
                <div
                  className={['dt-contract-sum__num', 'num', allOk ? 'is-ok' : 'is-warn'].join(' ')}
                >
                  {requiredSatisfied}/{requiredTotal}
                </div>
              </div>
              <div className="dt-contract-sum__box">
                <div className="dt-contract-sum__label">齐套率</div>
                <div className="dt-contract-sum__num num">{pctText(pct)}</div>
              </div>
            </div>

            {elements.map((el) => (
              <div className="dt-dv" key={el.elementId}>
                <div className="dt-dv__head">
                  <span className="dt-dv__icon">📎</span>
                  <span className="dt-dv__name">
                    {el.metricName ? `${el.metricName} / ` : ''}
                    {el.name}
                  </span>
                  <Badge tone={el.satisfied ? 'ok' : 'alert'}>
                    {el.satisfied ? `已上传 ${el.attachmentCount} 个附件` : '缺附件'}
                  </Badge>
                </div>
                {!el.satisfied && (
                  <div className="dt-dv__miss">⚠ 该要素要求附件，请到左侧「指标与要素」上传</div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
}
